import React, { useState, useEffect } from "react";
import { Modal, Button, Form, InputGroup, ListGroup } from "react-bootstrap";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import axios from "axios";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const LocationPicker = ({ setLatitude, setLongitude, setAddress, radius }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (search.length > 2) {
        fetchLocations();
      } else {
        setResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [search]);

  const fetchLocations = async () => {
    try {
      setLoadingSearch(true);

      const res = await axios.get(
        "https://nominatim.openstreetmap.org/search",
        {
          params: {
            q: search,
            format: "json",
            addressdetails: 1,
            limit: 5,
            countrycodes: "in",
          },
        },
      );

      setResults(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingSearch(false);
    }
  };

  const fetchAddress = async (lat, lon) => {
    try {
      const res = await axios.get(
        "https://nominatim.openstreetmap.org/reverse",
        {
          params: {
            lat,
            lon,
            format: "json",
          },
        },
      );

      if (res.data) {
        setSelectedAddress(res.data.display_name);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const MapUpdater = ({ coords }) => {
    const map = useMap();
    if (coords) {
      map.setView(coords, 15);
    }
    return null;
  };

  const LocationMarker = () => {
    useMapEvents({
      click(e) {
        setPosition(e.latlng);
        fetchAddress(e.latlng.lat, e.latlng.lng);
      },
    });

    return position ? (
      <>
        <Marker position={position} />
        <Circle center={position} radius={Number(radius) || 100} />
      </>
    ) : null;
  };

  const handleSelectLocation = (place) => {
    const lat = parseFloat(place.lat);
    const lon = parseFloat(place.lon);

    setPosition({ lat, lng: lon });
    setSelectedAddress(place.display_name);

    setResults([]);
    setSearch("");
  };

  const handleConfirm = () => {
    if (position) {
      setLatitude(position.lat);
      setLongitude(position.lng);
      setAddress(selectedAddress);
      setShow(false);
    }
  };

  return (
    <>
      <Button
        variant="outline-primary"
        className="rounded-pill px-4 shadow-sm"
        onClick={() => setShow(true)}
      >
        📍 Choose on Map
      </Button>

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Company Location</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <InputGroup className="mb-2">
            <Form.Control
              type="text"
              placeholder="Search city, area, landmark..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          {loadingSearch && (
            <div className="text-muted small mb-2">Searching...</div>
          )}
          {results.length > 0 && (
            <ListGroup className="mb-3 shadow-sm rounded">
              {results.map((place, idx) => {
                const main = place.display_name.split(",")[0];

                return (
                  <ListGroup.Item
                    key={idx}
                    action
                    onClick={() => handleSelectLocation(place)}
                    style={{ cursor: "pointer" }}
                    className="hover-shadow"
                  >
                    <div className="fw-semibold">{main}</div>
                    <div className="small text-muted">{place.display_name}</div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
          <MapContainer
            center={[28.6139, 77.209]}
            zoom={13}
            style={{ height: "400px", width: "100%" }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapUpdater coords={position} />
            <LocationMarker />
          </MapContainer>

          {position && (
            <div
              className="mt-3 p-3 rounded-3"
              style={{
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <div className="d-flex align-items-start gap-2">
                <span style={{ fontSize: "18px" }}>📍</span>

                <div>
                  <div className="fw-semibold text-success small">
                    Location Selected
                  </div>

                  <div className="small text-dark mt-1">
                    {selectedAddress || "Fetching address..."}
                  </div>

                  <div className="small text-muted mt-1">
                    Lat: {position.lat.toFixed(5)} | Lng:{" "}
                    {position.lng.toFixed(5)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirm}>
            Confirm Location
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .hover-shadow:hover {
            background-color: #f1f3f5;
            transition: 0.2s;
          }
        `}
      </style>
    </>
  );
};

export default LocationPicker;
